import { todayStr, toLocalDateStr } from "../lib/date";
import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Printer, Truck, Search, FileSpreadsheet } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { getWeeklySheet, saveWeeklySheetCell } from "../lib/api";
import { downloadFromApi } from "../lib/download";
import { printPage } from "../lib/print";

const DAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

// Color coding mirrors the source transportation spreadsheet.
const KIND_STYLE = {
  full: { background: "#B7E1CD", color: "#0d3d24" },      // full day  → green
  half: { background: "#D9EAD3", color: "#274d2c" },      // half day  → lighter green
  alt: { background: "#FFF2CC", color: "#7a5b00" },       // alt plan  → amber
  late: { background: "#FFF2CC", color: "#7a5b00" },      // late arrival → amber (shows time)
  early: { background: "#FFF2CC", color: "#7a5b00" },     // early departure → amber
  partial: { background: "#FFF2CC", color: "#7a5b00" },   // custom in–out window → amber
  absent: { background: "#F4CCCC", color: "#7a1f1f" },    // recorded absence → red
  pause: { background: "#E7E0F5", color: "#4b3b7a" },     // on pause → purple
  off: { background: "#FFFFFF", color: "#9ca3af" },        // not expected
  no_school: { background: "#F1F3F5", color: "#94a3b8", fontStyle: "italic" },
};
const OVERRIDE_STYLE = { background: "#FCE5CD", color: "#7a3e00" }; // manual free-text note

function mondayOf(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const day = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - day);
  return toLocalDateStr(d);
}
function addDays(dateStr, n) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + n);
  return toLocalDateStr(d);
}
function shortDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function WeeklySheetPage() {
  const [weekStart, setWeekStart] = useState(mondayOf(todayStr()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { studentId, date }
  const [editValue, setEditValue] = useState("");
  const [sortBy, setSortBy] = useState("name"); // "name" | "provider"
  const [providerFilter, setProviderFilter] = useState("all");
  const [nameQuery, setNameQuery] = useState("");

  const load = useCallback(async (ws) => {
    setLoading(true);
    try {
      const res = await getWeeklySheet(ws);
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load the Weekly Sheet");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(weekStart); }, [weekStart, load]);

  const providerOptions = useMemo(() => {
    const set = new Set();
    (data?.rows || []).forEach((r) => {
      if (r.provider) set.add(r.provider);
      if (r.second_provider) set.add(r.second_provider);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const visibleRows = useMemo(() => {
    let rows = data?.rows ? [...data.rows] : [];
    const q = nameQuery.trim().toLowerCase();
    if (q) rows = rows.filter((r) => r.name.toLowerCase().includes(q));
    if (providerFilter !== "all") {
      rows = rows.filter((r) => r.provider === providerFilter || r.second_provider === providerFilter);
    }
    rows.sort((a, b) => {
      if (sortBy === "provider") {
        const pa = (a.provider || "\uffff").toLowerCase();
        const pb = (b.provider || "\uffff").toLowerCase();
        if (pa !== pb) return pa.localeCompare(pb);
      }
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
    return rows;
  }, [data, nameQuery, providerFilter, sortBy]);

  const handleExport = async () => {
    if (!data) return;
    const params = new URLSearchParams({ week_start: weekStart, sort: sortBy });
    if (providerFilter !== "all") params.set("provider", providerFilter);
    if (nameQuery.trim()) params.set("q", nameQuery.trim());
    try {
      await downloadFromApi(`/api/weekly-sheet/export?${params.toString()}`, `weekly_sheet_${weekStart}.xlsx`);
    } catch (e) {
      toast.error("Could not export the Weekly Sheet");
    }
  };

  const cellStyle = (cell) => {    if (cell.override) return OVERRIDE_STYLE;
    return KIND_STYLE[cell.default_kind] || KIND_STYLE.off;
  };

  const beginEdit = (studentId, date, cell) => {
    setEditing({ studentId, date });
    setEditValue(cell.override || cell.default_label || "");
  };

  const commitEdit = async (row, date, cell) => {
    const value = editValue.trim();
    setEditing(null);
    // Sending the unchanged default (or empty) clears any override → back to auto.
    const send = (value === "" || value === (cell.default_label || "")) ? "" : value;
    if ((cell.override || "") === send) return; // no change
    // optimistic
    setData((prev) => {
      const rows = prev.rows.map((r) => {
        if (r.student_id !== row.student_id) return r;
        const cells = { ...r.cells };
        cells[date] = { ...cells[date], override: send || null, value: send || cells[date].default_label };
        return { ...r, cells };
      });
      return { ...prev, rows };
    });
    try {
      await saveWeeklySheetCell(row.student_id, date, send);
    } catch (e) {
      toast.error("Could not save that cell");
      load(weekStart);
    }
  };

  const handleKey = (e, row, date, cell) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commitEdit(row, date, cell); }
    if (e.key === "Escape") { setEditing(null); }
  };

  const weekLabel = data
    ? `${shortDate(data.dates[0])} – ${shortDate(data.dates[4])}, ${new Date(data.dates[0] + "T00:00:00").getFullYear()}`
    : "";

  return (
    <div className="p-4 md:p-6" data-testid="weekly-sheet-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Truck className="h-6 w-6 text-[#00305E]" />
          <div>
            <h1 className="text-2xl font-bold text-[#00305E] leading-tight">Weekly Sheet</h1>
            <p className="text-sm text-slate-500">Weekly Transportation &amp; Attendance Overview</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" data-testid="weekly-prev-week"
            onClick={() => setWeekStart(addDays(weekStart, -7))}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="min-w-[170px] text-center font-semibold text-slate-700" data-testid="weekly-week-label">{weekLabel}</span>
          <Button variant="outline" size="icon" data-testid="weekly-next-week"
            onClick={() => setWeekStart(addDays(weekStart, 7))}><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" className="ml-1"
            onClick={() => setWeekStart(mondayOf(todayStr()))}
            data-testid="weekly-this-week">This week</Button>
          <Button size="sm" variant="outline" data-testid="weekly-export-btn"
            onClick={handleExport} disabled={!data}>
            <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
          </Button>
          <Button size="sm" className="bg-[#00305E] hover:bg-[#004488]" data-testid="weekly-print-btn"
            onClick={() => printWeeklySheet({ ...data, rows: visibleRows }, weekLabel)} disabled={!data}>
            <Printer className="h-4 w-4 mr-1" /> Print
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={nameQuery} onChange={(e) => setNameQuery(e.target.value)}
            placeholder="Search student…" className="pl-8 w-[200px] h-9"
            data-testid="weekly-name-search" />
        </div>
        <Select value={providerFilter} onValueChange={setProviderFilter}>
          <SelectTrigger className="w-[220px] h-9" data-testid="weekly-provider-filter">
            <SelectValue placeholder="All transportation" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All transportation</SelectItem>
            {providerOptions.map((p) => (
              <SelectItem key={p} value={p} data-testid={`provider-opt-${p}`}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[170px] h-9" data-testid="weekly-sort-by">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name">Sort: Name</SelectItem>
            <SelectItem value="provider">Sort: Provider</SelectItem>
          </SelectContent>
        </Select>
        {(nameQuery || providerFilter !== "all") && (
          <>
            <Button variant="ghost" size="sm" className="h-9 text-slate-500"
              onClick={() => { setNameQuery(""); setProviderFilter("all"); }}
              data-testid="weekly-clear-filters">Clear</Button>
            <span className="text-xs text-slate-500" data-testid="weekly-result-count">
              {visibleRows.length} student{visibleRows.length === 1 ? "" : "s"}
            </span>
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mb-3 text-xs text-slate-600">
        <Legend swatch="#B7E1CD" label="Full day" />
        <Legend swatch="#D9EAD3" label="Half day" />
        <Legend swatch="#FFF2CC" label="Late in / early out / alt plan" />
        <Legend swatch="#F4CCCC" label="Absent" />
        <Legend swatch="#E7E0F5" label="On pause" />
        <Legend swatch="#FCE5CD" label="Staff note (override)" />
        <span className="text-slate-400">Click any day cell to edit · Enter to save · Esc to cancel · clear to reset</span>
      </div>

      {loading ? (
        <div className="text-slate-500 py-10 text-center">Loading…</div>
      ) : !data || data.rows.length === 0 ? (
        <div className="text-slate-500 py-10 text-center border rounded-lg">No current students to display.</div>
      ) : (
        <div className="overflow-auto border rounded-lg max-h-[calc(100vh-220px)]">
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="bg-[#00305E] text-white">
                <th className="px-3 py-2 text-left font-semibold sticky left-0 bg-[#00305E] z-20 min-w-[160px]">Student</th>
                <th className="px-3 py-2 text-left font-semibold min-w-[180px]">Transportation Contact</th>
                <th className="px-3 py-2 text-center font-semibold min-w-[90px]">Pick-up</th>
                {data.dates.map((d, i) => {
                  const m = data.day_meta[d];
                  return (
                    <th key={d} className="px-2 py-2 text-center font-semibold min-w-[120px]">
                      <div>{DAY_LABELS[i]}</div>
                      <div className="text-[11px] font-normal opacity-80">{shortDate(d)}</div>
                      {!m.is_school_day ? (
                        <div className="text-[10px] font-normal text-amber-200">No school</div>
                      ) : m.is_half_day ? (
                        <div className="text-[10px] font-normal text-emerald-200">Half day</div>
                      ) : null}
                    </th>
                  );
                })}
                <th className="px-3 py-2 text-left font-semibold min-w-[200px]">Transportation Address</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-slate-500 py-10 text-center" data-testid="weekly-no-matches">
                    No students match the current filters.
                  </td>
                </tr>
              ) : visibleRows.map((row, ri) => (
                <tr key={row.student_id} className={ri % 2 ? "bg-slate-50" : "bg-white"}>
                  <td className="px-3 py-2 font-semibold text-slate-800 sticky left-0 z-10 whitespace-nowrap"
                    style={{ background: ri % 2 ? "#f8fafc" : "#fff" }} data-testid={`row-name-${row.student_id}`}>
                    {row.name}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600 whitespace-pre-line">{row.transportation_contact || "—"}</td>
                  <td className="px-3 py-2 text-center text-xs text-slate-600">{row.pickup_time || "—"}</td>
                  {data.dates.map((d) => {
                    const cell = row.cells[d];
                    const isEditing = editing && editing.studentId === row.student_id && editing.date === d;
                    return (
                      <td key={d} className="p-0 border-l border-slate-200 align-top">
                        {isEditing ? (
                          <textarea
                            autoFocus
                            data-testid={`cell-input-${row.student_id}-${d}`}
                            className="w-full h-full min-h-[42px] p-1 text-xs border-2 border-[#00305E] outline-none resize-none"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={() => commitEdit(row, d, cell)}
                            onKeyDown={(e) => handleKey(e, row, d, cell)}
                          />
                        ) : (
                          <div
                            data-testid={`cell-${row.student_id}-${d}`}
                            onClick={() => beginEdit(row.student_id, d, cell)}
                            className="min-h-[42px] px-2 py-1 text-xs cursor-text whitespace-pre-line flex items-center justify-center text-center hover:ring-2 hover:ring-[#00305E]/30"
                            style={cellStyle(cell)}
                            title="Click to edit"
                          >
                            {cell.value || ""}
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-xs text-slate-600 border-l border-slate-200 whitespace-pre-line">{row.home_address || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Legend = ({ swatch, label }) => (
  <span className="inline-flex items-center gap-1">
    <span className="inline-block w-3 h-3 rounded-sm border border-slate-300" style={{ background: swatch }} />
    {label}
  </span>
);

function printWeeklySheet(data, weekLabel) {
  if (!data) return;
  const KS = {
    full: "background:#B7E1CD;color:#0d3d24",
    half: "background:#D9EAD3;color:#274d2c",
    alt: "background:#FFF2CC;color:#7a5b00",
    late: "background:#FFF2CC;color:#7a5b00",
    early: "background:#FFF2CC;color:#7a5b00",
    partial: "background:#FFF2CC;color:#7a5b00",
    absent: "background:#F4CCCC;color:#7a1f1f",
    pause: "background:#E7E0F5;color:#4b3b7a",
    off: "background:#fff;color:#999",
    no_school: "background:#F1F3F5;color:#94a3b8;font-style:italic",
  };
  const OVR = "background:#FCE5CD;color:#7a3e00";
  const headCells = DAY_LABELS.map((lbl, i) => {
    const d = data.dates[i]; const m = data.day_meta[d];
    const note = !m.is_school_day ? "<br/><span style='font-size:8px'>No school</span>"
      : (m.is_half_day ? "<br/><span style='font-size:8px'>Half day</span>" : "");
    return `<th>${lbl}<br/><span style="font-weight:400;font-size:9px">${shortDate(d)}</span>${note}</th>`;
  }).join("");
  const rows = data.rows.map((r) => {
    const dayTds = data.dates.map((d) => {
      const c = r.cells[d];
      const style = c.override ? OVR : (KS[c.default_kind] || KS.off);
      return `<td class="ws-cell" style="${style};text-align:center">${(c.value || "").replace(/\n/g, "<br/>")}</td>`;
    }).join("");
    return `<tr>
      <td style="font-weight:600;white-space:nowrap">${r.name}</td>
      <td style="font-size:9px">${(r.transportation_contact || "").replace(/\n/g, "<br/>")}</td>
      <td style="text-align:center;font-size:9px">${r.pickup_time || ""}</td>
      ${dayTds}
      <td style="font-size:9px">${(r.home_address || "").replace(/\n/g, "<br/>")}</td>
    </tr>`;
  }).join("");
  const content = `<table class="ws-table"><thead><tr>
      <th>Student</th><th>Transportation Contact</th><th style="text-align:center">Pick-up</th>
      ${headCells}<th>Transportation Address</th>
    </tr></thead><tbody>${rows}</tbody></table>`;
  printPage({
    title: "Weekly Transportation & Attendance Overview",
    subtitle: weekLabel,
    content,
    landscape: true,
    showFooter: false,
    printDelay: 300,
    extraStyles: `
      .ws-table { width:100%; border-collapse:collapse; font-size:11px; margin:0; }
      .ws-table th, .ws-table td { border:1px solid #cbd5e1; padding:5px 6px; }
      .ws-table th { background:#00305E !important; color:#fff !important; text-align:left; font-size:10px; }
      .ws-cell { print-color-adjust:exact !important; -webkit-print-color-adjust:exact !important; }
    `,
  });
}
